import { Component, lazy, Suspense, useEffect, type ErrorInfo, type ReactNode } from 'react';
import HiddenMapPrewarmer from './HiddenMapPrewarmer';
import { useAssetPreloader } from '../hooks/useAssetPreloader';
import { useParams } from 'react-router-dom';
import { useAutoCycle } from '../hooks/useAutoCycle';
import type { CircuitStat, ShowcaseData, ShowcaseScreen } from '../hooks/useShowcaseData';
import { useShowcaseData } from '../hooks/useShowcaseData';
import BrandedWelcome from '../screens/BrandedWelcome';
import Countdown from '../screens/Countdown';
import DriverProfile from '../screens/DriverProfile';
import DriverQuote from '../screens/DriverQuote';
import LiveClock from '../screens/LiveClock';
import WeatherDashboard from '../screens/WeatherDashboard';
import ErrorScreen from './ErrorScreen';
import LoadingScreen from './LoadingScreen';
import ScreenTransition from './ScreenTransition';
import ShowcaseHeader from './ShowcaseHeader';
import './ShowcasePlayer.css';

// Lazy-load heavy screens — deck.gl + MapLibre (~530KB) only loads when needed
const CircuitInfo = lazy(() => import('../screens/CircuitInfo'));

const DEFAULT_PRIMARY_COLOR = '#6366f1';
const ORG_FONT_FACE_FAMILY = 'RaceProXShowcaseOrgFont';

export interface ShowcaseBrand {
  primaryColor: string;
  secondaryColor: string | null;
  logoUrl: string | null;
  backgroundUrl: string | null;
  fontFamily: string | null;
  fontCssFamily: string;
  fontFaceUrl: string | null;
}

function escapeCssString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '');
}

function isFontFileReference(value: string): boolean {
  return (
    /^https?:\/\//i.test(value) ||
    value.startsWith('/') ||
    /\.(woff2?|ttf|otf)([?#].*)?$/i.test(value)
  );
}

function resolveFontCssFamily(fontFamily: string | null, fontFaceUrl: string | null): string {
  if (!fontFamily) {
    return 'var(--font-main)';
  }

  if (fontFaceUrl) {
    return `"${ORG_FONT_FACE_FAMILY}", var(--font-main)`;
  }

  if (fontFamily.includes(',') || fontFamily.startsWith('"') || fontFamily.startsWith("'")) {
    return fontFamily;
  }

  return `"${escapeCssString(fontFamily)}", var(--font-main)`;
}

function resolveShowcaseBrand(showcase: ShowcaseData): ShowcaseBrand {
  const fontFamily = showcase.organization?.fontFamily ?? null;
  const fontFaceUrl =
    fontFamily && isFontFileReference(fontFamily) ? fontFamily : null;

  return {
    primaryColor:
      showcase.themeOverrides?.primaryColor ??
      showcase.organization?.primaryColor ??
      DEFAULT_PRIMARY_COLOR,
    secondaryColor: showcase.organization?.secondaryColor ?? null,
    logoUrl:
      showcase.themeOverrides?.logoUrl ?? showcase.organization?.logoUrl ?? null,
    backgroundUrl:
      showcase.themeOverrides?.backgroundUrl ??
      showcase.organization?.backgroundImageUrl ??
      null,
    fontFamily,
    fontCssFamily: resolveFontCssFamily(fontFamily, fontFaceUrl),
    fontFaceUrl,
  };
}

/* ---------- Error Boundary ---------- */

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ScreenErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ShowcasePlayer] Screen render error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

/* ---------- Screen Props ---------- */

/** Common props passed to every screen component via the registry. */
export interface ScreenProps {
  orgName: string;
  logoUrl: string | null;
  primaryColor: string;
  brand: ShowcaseBrand;
  eventName: string | null;
  circuitName: string | null;
  countryCode: string | null;
  timezone: string | null;
  config: Record<string, unknown> | null;
  // Circuit data — populated from showcase.event.circuit
  circuitLatitude: number | null;
  circuitLongitude: number | null;
  circuitLayout: number[][] | null;
  circuitStats: CircuitStat[] | null;
}

/* ---------- Screen Registry ---------- */

/**
 * Registry-based screen resolution.
 *
 * Maps screen type slugs → React components. Scales cleanly to 13+ screens
 * and pairs naturally with React.lazy() for code-splitting heavy screens
 * (e.g. 3D showroom, telemetry) in later phases.
 */
const screenRegistry: Record<string, React.ComponentType<ScreenProps>> = {
  'branded-welcome': ({ orgName, logoUrl }) => (
    <BrandedWelcome
      orgName={orgName}
      logoUrl={logoUrl}
    />
  ),
  'live-clock': ({ orgName, logoUrl, eventName, circuitName, timezone }) => (
    <LiveClock
      orgName={orgName}
      logoUrl={logoUrl}
      eventName={eventName}
      circuitName={circuitName}
      timezone={timezone}
    />
  ),
  'weather': ({
    orgName,
    logoUrl,
    primaryColor,
    eventName,
    circuitName,
    countryCode,
    timezone,
    circuitLatitude,
    circuitLongitude,
  }) => (
    <WeatherDashboard
      orgName={orgName}
      logoUrl={logoUrl}
      primaryColor={primaryColor}
      eventName={eventName}
      circuitName={circuitName}
      countryCode={countryCode}
      timezone={timezone}
      circuitLatitude={circuitLatitude}
      circuitLongitude={circuitLongitude}
    />
  ),
  'circuit': (props) => (
    <Suspense fallback={<div style={{ width: '100vw', height: '100vh', background: '#0a0c0f' }} />}>
      <CircuitInfo {...props} />
    </Suspense>
  ),
  'driver-profile': ({ orgName, primaryColor, config }) => (
    <DriverProfile
      orgName={orgName}
      primaryColor={primaryColor}
      config={config}
    />
  ),
  'driver-quote': ({ orgName, primaryColor, config }) => (
    <DriverQuote
      orgName={orgName}
      primaryColor={primaryColor}
      config={config}
    />
  ),
  'countdown': ({ orgName, logoUrl, primaryColor, eventName, timezone, config }) => (
    <Countdown
      orgName={orgName}
      logoUrl={logoUrl}
      primaryColor={primaryColor}
      eventName={eventName}
      timezone={timezone}
      config={config}
    />
  ),
};

/* ---------- Screen Resolver ---------- */

interface ScreenRendererProps {
  screen: ShowcaseScreen;
  showcase: ShowcaseData;
}

function ScreenRenderer({ screen, showcase }: ScreenRendererProps) {
  const orgName = showcase.organization?.name ?? 'RaceProX';
  const brand = resolveShowcaseBrand(showcase);
  const eventName = showcase.event?.name ?? null;
  const circuitName = showcase.event?.circuit?.name ?? null;
  const countryCode = showcase.event?.circuit?.countryCode ?? null;
  const timezone = showcase.event?.timezone ?? showcase.event?.circuit?.timezone ?? null;

  // Circuit data for the map screen
  const circuit = showcase.event?.circuit;

  const props: ScreenProps = {
    orgName,
    logoUrl: brand.logoUrl,
    primaryColor: brand.primaryColor,
    brand,
    eventName,
    circuitName,
    countryCode,
    timezone,
    config: screen.config,
    circuitLatitude: circuit?.latitude ?? null,
    circuitLongitude: circuit?.longitude ?? null,
    circuitLayout: circuit?.layout ?? null,
    circuitStats: circuit?.stats ?? null,
  };

  // Consistent header title/subtitle across all screens
  const headerTitle = circuitName ?? eventName ?? orgName;
  const headerSubtitle = eventName && circuitName ? eventName : undefined;

  // Look up the screen component from the registry, fall back to branded-welcome
  const ScreenComponent = screenRegistry[screen.screenType] ?? screenRegistry['branded-welcome'];

  return (
    <div style={{
      position: 'relative',
      width: '100vw',
      height: '100vh',
      // Header clearance: wrapper top-padding (max 4rem) + header (12rem) + gap (2rem) = 18rem
      '--header-height': '18rem',
    } as React.CSSProperties}>
      {/* ── Shared header — rendered once for every screen ──────────── */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 50, padding: 'clamp(1.6rem, 2vh, 4rem) clamp(1.6rem, 2vw, 4rem) 0' }}>
        <ShowcaseHeader
          logoUrl={brand.logoUrl}
          orgName={orgName}
          title={headerTitle}
          subtitle={headerSubtitle}
          countryCode={countryCode}
          timezone={timezone}
        />
      </div>
      <ScreenComponent {...props} />
    </div>
  );
}

/* ---------- Player Content ---------- */

interface PlayerContentProps {
  showcase: ShowcaseData;
}

function PlayerContent({ showcase }: PlayerContentProps) {
  const { screens, loop, transitionType, transitionDurationMs } = showcase;
  const brand = resolveShowcaseBrand(showcase);

  // Request fullscreen on mount — most TV browsers honour this.
  // Silent fail if unsupported; the PWA manifest handles kiosk mode as fallback.
  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen && !document.fullscreenElement) {
      el.requestFullscreen().catch(() => { /* TV browser may not support it */ });
    }
  }, []);

  const { currentIndex, isTransitioning } = useAutoCycle(screens, {
    loop,
    transitionDurationMs,
  });

  const currentScreen = screens[currentIndex];

  if (!currentScreen) {
    return <ErrorScreen message="No screens configured for this showcase." />;
  }

  const brandingStyle = {
    '--primary': brand.primaryColor,
    '--sc-primary': brand.primaryColor,
    '--sc-secondary': brand.secondaryColor ?? brand.primaryColor,
    '--sc-font-family': brand.fontCssFamily,
    '--sc-event-font-family': brand.fontCssFamily,
    ...(brand.backgroundUrl
      ? {
          backgroundImage: `linear-gradient(rgba(10,10,15,0.85), rgba(10,10,15,0.92)), url(${brand.backgroundUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {}),
  } as React.CSSProperties;

  return (
    <div className="showcase-player" style={brandingStyle}>
      {brand.fontFaceUrl && (
        <style>
          {`@font-face{font-family:"${ORG_FONT_FACE_FAMILY}";src:url("${escapeCssString(brand.fontFaceUrl)}");font-style:normal;font-display:swap;}`}
        </style>
      )}
      <ScreenTransition
        type={transitionType}
        duration={transitionDurationMs}
        isTransitioning={isTransitioning}
      >
        <ScreenErrorBoundary
          key={currentScreen.id}
          fallback={
            <ErrorScreen
              title="Screen Error"
              message="This screen encountered an error and cannot be displayed."
            />
          }
        >
          <ScreenRenderer screen={currentScreen} showcase={showcase} />
        </ScreenErrorBoundary>
      </ScreenTransition>
    </div>
  );
}

/* ---------- ShowcasePlayer (entry) ---------- */

export default function ShowcasePlayer() {
  const { token } = useParams<{ token: string }>();
  const { showcase, isLoading, error } = useShowcaseData(token);
  const preloader = useAssetPreloader(isLoading ? null : showcase);

  // Phase 1: Data loading (RPC fetch)
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !showcase) {
    return <ErrorScreen message={error ?? 'Showcase not found.'} />;
  }

  if (showcase.screens.length === 0) {
    return <ErrorScreen message="No screens configured for this showcase." />;
  }

  // Phase 2: Asset preloading (GLBs, images, fonts, map tiles)
  if (!preloader.ready) {
    return (
      <>
        <LoadingScreen progress={preloader.progress} label={preloader.label} />
        {/* Mount map offscreen to pre-warm tiles while loading screen is visible */}
        {preloader.needsMapPrewarm && preloader.mapPrewarmData && (
          <HiddenMapPrewarmer
            trackPath={preloader.mapPrewarmData.trackPath}
            center={preloader.mapPrewarmData.center}
            mapStyleId={preloader.mapPrewarmData.mapStyleId}
            trackColor={preloader.mapPrewarmData.trackColor}
            onComplete={preloader.onMapPrewarmComplete}
          />
        )}
      </>
    );
  }

  // Phase 3: Playback — all assets cached, transitions are instant
  return <PlayerContent showcase={showcase} />;
}
